import Wand2 from 'lucide-react/dist/esm/icons/wand-2';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import { useState, useRef, useEffect, ReactNode } from 'react';
import { Group, Stack, Box, Text, ActionIcon, Checkbox, Button, Tooltip, Divider, Menu, Slider, Switch, NumberInput } from '@mantine/core';
import X from 'lucide-react/dist/esm/icons/x';
import Folder from 'lucide-react/dist/esm/icons/folder';
import FolderPlus from 'lucide-react/dist/esm/icons/folder-plus';
import Film from 'lucide-react/dist/esm/icons/film';
import Music from 'lucide-react/dist/esm/icons/music';
import Video from 'lucide-react/dist/esm/icons/video';
import Zap from 'lucide-react/dist/esm/icons/zap';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import CropIcon from 'lucide-react/dist/esm/icons/crop';
import Paintbrush from 'lucide-react/dist/esm/icons/paintbrush';
import ArrowUpLeft from 'lucide-react/dist/esm/icons/arrow-up-left';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import ArrowDownLeft from 'lucide-react/dist/esm/icons/arrow-down-left';
import ArrowDownRight from 'lucide-react/dist/esm/icons/arrow-down-right';
import type { Segment, MediaBinItem, OverlayConfig, CropConfig, MaskConfig } from '../types';
import { makeDefaultOverlay, makeDefaultMask, formatTime, parseTime, REL_VOLUME_MIN, REL_VOLUME_MAX, getSegmentOverlays, getSegmentMasks, isAudioFile, getPipSnapCoords, defaultPipCoords, isOverlaySnapped, PIP_CORNERS, PIP_DEFAULT_W, type PipCorner } from '../utils';
import classes from '../editor.module.css';

interface Props {
  seg: Segment; mediaBin: MediaBinItem[]; onUpdate: (patch: Partial<Segment>) => void; onRemove: () => void;
  onBrowseVideo: () => void; onBrowseOverlay: () => void; onSelectVideoFromBin: (path: string) => void;
  isCropping?: boolean; onToggleCropping?: () => void; onFitCanvas?: (w: number, h: number) => void;
  onBrowseAudio?: () => void; onAutoSync?: (m: string, s: string, segId: string, ovId?: string) => void;
  onAutoColorGrade?: (videoPath: string, segId: string, ovId: string, crop?: CropConfig | null) => void;
  canvasWidth?: number;
  canvasHeight?: number;
  currentTime?: number;
  isMasking?: boolean;
  activeMaskId?: string | null;
  onToggleMasking?: (maskId?: string) => void;
  detectingMaskId?: string | null;
  onAutoDetectSlide?: (maskId: string) => void;
}

const fileName = (p: string) => (p ? p.split('/').pop() ?? p : '');

const COLOR_SLIDERS = [
  { key: 'lift', label: 'Lift', tip: 'Сдвиг теней (-0.3..+0.3)', min: -0.3, max: 0.3, step: 0.01, def: 0, sign: true },
  { key: 'gamma', label: 'Gamma', tip: 'Гамма полутонов (1.0)', min: 0.2, max: 3.0, step: 0.05, def: 1, sign: false },
  { key: 'gain', label: 'Gain', tip: 'Контрастность светов (1.0)', min: 0.5, max: 2.5, step: 0.05, def: 1, sign: false },
] as const;

function CropSliders({ crop, srcW, srcH, onChange }: { crop?: CropConfig | null; srcW: number; srcH: number; onChange: (patch: CropConfig) => void }) {
  const cur = { top: crop?.top || 0, bottom: crop?.bottom || 0, left: crop?.left || 0, right: crop?.right || 0 };
  const items = [
    { key: 'bottom' as const, label: 'Снизу:', val: cur.bottom, max: Math.floor(srcH * 0.48) },
    { key: 'top' as const, label: 'Сверху:', val: cur.top, max: Math.floor(srcH * 0.48) },
    { key: 'left' as const, label: 'Слева:', val: cur.left, max: Math.floor(srcW * 0.48) },
    { key: 'right' as const, label: 'Справа:', val: cur.right, max: Math.floor(srcW * 0.48) },
  ];
  return (
    <Stack gap={4}>
      {items.map(({ key, label, val, max }) => (
        <Group key={key} gap="xs" wrap="nowrap" align="center">
          <Text fz="xs" w={55} style={{ flexShrink: 0 }}>{label}</Text>
          <Slider style={{ flex: 1 }} size="xs" min={0} max={max} step={2} value={val} label={v => `${v} px`} onChange={v => onChange({ ...cur, [key]: v })} />
          <NumberInput size="xs" w={70} min={0} max={max} step={2} value={val} onChange={v => onChange({ ...cur, [key]: Math.max(0, (Number(v) || 0) & ~1) })} clampBehavior="strict" styles={{ input: { textAlign: 'right', padding: '0 4px', fontSize: 11, height: 24, minHeight: 24 } }} />
          <Text fz="xs" c="dimmed" style={{ flexShrink: 0 }}>px</Text>
        </Group>
      ))}
    </Stack>
  );
}

function FolderSelectMenu({ label, tooltip, mediaBin, onSelectBin, onBrowseDisk, icon = <Folder size={16} />, text, color = "gray", variant = "subtle" }: { label: string; tooltip?: string; mediaBin: MediaBinItem[]; onSelectBin: (p: string) => void; onBrowseDisk: () => void; icon?: ReactNode; text?: string; color?: string; variant?: string }) {
  const button = text ? <Button variant={variant as any} color={color} size="compact-sm" leftSection={icon}>{text}</Button> : <ActionIcon variant={variant as any} color={color} aria-label={label}>{icon}</ActionIcon>;
  const hint = tooltip ?? (text ? undefined : label);
  const trigger = <span style={{ display: 'inline-flex' }}>{hint ? <Tooltip label={hint} withArrow openDelay={300}>{button}</Tooltip> : button}</span>;
  if (mediaBin.length === 0) return <Box onClick={onBrowseDisk}>{trigger}</Box>;
  return (
    <Menu withinPortal position="bottom-end">
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Из корзины проекта</Menu.Label>
        {mediaBin.map(i => (
          <Menu.Item key={i.id} leftSection={isAudioFile(i.name) || !i.width ? <Music size={14} /> : <Film size={14} />} onClick={() => onSelectBin(i.path)}>
            <Text fz="xs" truncate maw={240}>{i.name}</Text>
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item leftSection={<FolderPlus size={14} />} onClick={onBrowseDisk}>Выбрать с диска...</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function InlineTime({ value, onCommit, placeholder = "00:00:00" }: { value: string; onCommit: (v: string) => void; placeholder?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select(); } }, [editing]);
  if (editing) {
    return (
      <input ref={ref} className={classes.timeInput} value={draft} onChange={e => setDraft(e.currentTarget.value)}
        onBlur={() => { onCommit(draft.trim()); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { onCommit(draft.trim()); setEditing(false); } else if (e.key === 'Escape') setEditing(false); }} />
    );
  }
  return <span className={classes.timeValue} data-empty={!value || undefined} onClick={() => { setDraft(value); setEditing(true); }}>{value || placeholder}</span>;
}

function TimeRange({ label, tooltip, start, end, onStart, onEnd, placeholderEnd = "00:00:00" }: { label: string; tooltip?: string; start: string; end: string; onStart: (v: string) => void; onEnd: (v: string) => void; placeholderEnd?: string }) {
  return (
    <Group gap={4} wrap="nowrap" align="center">
      {tooltip ? <Tooltip label={tooltip} withArrow><Text fz="xs" c="dimmed" style={{ cursor: 'help' }}>{label}:</Text></Tooltip> : <Text fz="xs" c="dimmed">{label}:</Text>}
      <InlineTime value={start} onCommit={onStart} />
      <Text c="dimmed">—</Text>
      <InlineTime value={end} placeholder={placeholderEnd} onCommit={onEnd} />
    </Group>
  );
}

export function SegmentCard({
  seg, mediaBin, onUpdate, onRemove, onBrowseVideo, onBrowseOverlay, onSelectVideoFromBin,
  isCropping, onToggleCropping, onFitCanvas, onBrowseAudio, onAutoSync, onAutoColorGrade,
  canvasWidth = 1920, canvasHeight = 1080, currentTime = 0, isMasking, activeMaskId, onToggleMasking,
  detectingMaskId, onAutoDetectSlide,
}: Props) {
  const crop = seg.crop;
  const hasCrop = Boolean(crop && (crop.top || crop.bottom || crop.left || crop.right));
  const currentBin = mediaBin.find(x => x.path === seg.video);
  const srcW = currentBin?.width || 1920, srcH = currentBin?.height || 1080;
  const effW = Math.max(16, srcW - (crop?.left || 0) - (crop?.right || 0)), effH = Math.max(16, srcH - (crop?.top || 0) - (crop?.bottom || 0));
  const hasCustomAudio = Boolean(seg.audio && seg.audio !== seg.video);
  const overlays = getSegmentOverlays(seg);
  const masks = getSegmentMasks(seg);
  const [maskOpen, setMaskOpen] = useState(masks.length > 0);

  function snapOverlay(ov: OverlayConfig, corner: PipCorner) {
    const camBin = mediaBin.find(x => x.path === ov.video);
    const camW = camBin?.width || 1920;
    const camH = camBin?.height || 1080;
    const coords = getPipSnapCoords(corner, ov.w || PIP_DEFAULT_W, camW, camH, canvasWidth, canvasHeight, ov.crop);
    patchOverlay(ov.id || '', coords);
  }

  function addOverlay(path: string) {
    const dur = mediaBin.find(x => x.path === path)?.duration || 1790.37;
    const tlStart = overlays[overlays.length - 1]?.timeline_end || seg.start || '00:00:00';
    const camBin = mediaBin.find(x => x.path === path);
    const snapCoords = defaultPipCoords(camBin?.width, camBin?.height, canvasWidth, canvasHeight);
    const newOv = { ...makeDefaultOverlay(path, tlStart, formatTime(parseTime(tlStart) + dur)), ...snapCoords, start: '00:00:00', end: formatTime(dur) };
    const next = [...overlays, newOv];
    onUpdate({ overlays: next, overlay: next[0] });
  }

  function patchOverlay(id: string, patch: Partial<OverlayConfig>) {
    const next = overlays.map(o => (o.id === id ? { ...o, ...patch } : o));
    onUpdate({ overlays: next, overlay: next[0] || null });
  }

  function addMask() {
    const startSec = Math.max(0, currentTime);
    const newM = makeDefaultMask(formatTime(startSec), formatTime(startSec + 10));
    const next = [...masks, newM];
    onUpdate({ masks: next });
    setMaskOpen(true);
    onToggleMasking?.(newM.id);
  }

  function patchMask(id: string, patch: Partial<MaskConfig>) {
    const next = masks.map(m => m.id === id ? { ...m, ...patch } : m);
    onUpdate({ masks: next });
  }

  function deleteMask(id: string) {
    const next = masks.filter(m => m.id !== id);
    onUpdate({ masks: next });
    if (activeMaskId === id) onToggleMasking?.(undefined);
  }

  return (
    <Box style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-md)', padding: 'var(--mantine-spacing-sm)' }}>
      <Group justify="space-between" wrap="nowrap" gap="xs" mb={4}>
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Group gap={6} wrap="nowrap">
            <Film size={16} style={{ color: 'var(--mantine-color-brand-4)', flexShrink: 0 }} />
            <Text fw={600} truncate>{seg.video ? fileName(seg.video) : <Text span c="dimmed">Выберите видео</Text>}</Text>
          </Group>
          {hasCustomAudio && <Group gap={6} wrap="nowrap"><Music size={13} style={{ color: 'var(--mantine-color-blue-4)', flexShrink: 0 }} /><Text fz="xs" c="blue" truncate>Звук: {fileName(seg.audio)}</Text></Group>}
        </Stack>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          <FolderSelectMenu label="Звук" tooltip={hasCustomAudio ? "Звук взят из другого файла: заменить" : "Взять звук из другого файла"} icon={<Music size={16} />} mediaBin={mediaBin} onSelectBin={p => onUpdate({ audio: p })} onBrowseDisk={onBrowseAudio || onBrowseVideo} color={hasCustomAudio ? "blue" : "gray"} variant={hasCustomAudio ? "light" : "subtle"} />
          <FolderSelectMenu label="Видео" tooltip="Заменить видео фрагмента" icon={<Folder size={16} />} mediaBin={mediaBin} onSelectBin={onSelectVideoFromBin} onBrowseDisk={onBrowseVideo} />
          <Tooltip label="Удалить фрагмент" withArrow openDelay={300}><ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Удалить фрагмент"><X size={16} /></ActionIcon></Tooltip>
        </Group>
      </Group>

      {/* 1. Хронометраж отрезка */}
      <Group justify="space-between" align="center" wrap="nowrap" mt="xs">
        <Group gap={6} wrap="nowrap" align="center">
          <Text fz="xs" c="dimmed">Отрезок:</Text>
          <InlineTime value={seg.start} onCommit={v => onUpdate({ start: v })} />
          <Text c="dimmed">—</Text>
          <InlineTime value={seg.end} onCommit={v => onUpdate({ end: v })} />
        </Group>
      </Group>

      {/* 2. Инструменты отрезка (Crop, Замазка, PiP) */}
      <Group gap={6} wrap="nowrap" align="center" mt={6}>
        <Button
          variant={hasCrop ? "filled" : isCropping ? "light" : "subtle"}
          color={hasCrop ? "blue" : "gray"}
          size="compact-sm"
          leftSection={<CropIcon size={14} />}
          onClick={() => { if (!crop) onUpdate({ crop: { top: 0, bottom: 0, left: 0, right: 0 } }); onToggleCropping?.(); }}
        >
          {hasCrop ? `Crop (${crop?.bottom ? `${crop.bottom}↓` : ''}${crop?.top ? ` ${crop.top}↑` : ''})` : 'Crop'}
        </Button>
        <Button
          variant={masks.length > 0 ? (maskOpen ? "filled" : "light") : "subtle"}
          color={masks.length > 0 ? "teal" : "gray"}
          size="compact-sm"
          leftSection={<Paintbrush size={14} />}
          onClick={() => { if (!masks.length) addMask(); else setMaskOpen(o => !o); }}
        >
          {masks.length > 0 ? `Замазка (${masks.length})` : 'Замазка'}
        </Button>
        <FolderSelectMenu
          label="PiP"
          text={overlays.length > 0 ? `+ PiP (${overlays.length})` : "+ PiP"}
          icon={<Video size={14} />}
          mediaBin={mediaBin}
          onSelectBin={addOverlay}
          onBrowseDisk={onBrowseOverlay}
          variant={overlays.length > 0 ? "light" : "subtle"}
          color={overlays.length > 0 ? "pink" : "gray"}
        />
      </Group>

      <Group gap="xs" wrap="nowrap" align="center" mt="xs">
        <Tooltip label="Множитель громкости фрагмента" withArrow position="left"><Text fz="xs" c="dimmed" style={{ flexShrink: 0, cursor: 'help' }}>Отн. громкость:</Text></Tooltip>
        <Slider style={{ flex: 1 }} size="xs" min={REL_VOLUME_MIN} max={REL_VOLUME_MAX} step={0.05} value={seg.volume} label={v => `${v.toFixed(2)}x`} onChange={v => onUpdate({ volume: v })} />
        <Text fz="xs" c="dimmed" w={42} ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{seg.volume.toFixed(2)}x</Text>
      </Group>

      {(isCropping || hasCrop) && (
        <Stack gap="xs" mt="sm" p="xs" style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 'var(--mantine-radius-sm)' }}>
          <Group justify="space-between" align="center">
            <Group gap={6}><CropIcon size={15} style={{ color: 'var(--mantine-color-brand-5)' }} /><Text fz="xs" fw={600}>Кадрирование основного видео</Text></Group>
            <Group gap="xs"><Text fz="xs" c="dimmed">{effW} × {effH}</Text>{hasCrop && <Button variant="subtle" color="gray" size="compact-xs" onClick={() => onUpdate({ crop: null })}>Сброс</Button>}</Group>
          </Group>
          <Group gap="xs">
            <Button size="compact-xs" variant="light" color="gray" onClick={() => onUpdate({ crop: { top: crop?.top || 0, bottom: Math.round(srcH * 0.045 / 2) * 2, left: crop?.left || 0, right: crop?.right || 0 } })}>Таскбар снизу</Button>
            <Button size="compact-xs" variant="light" color="gray" onClick={() => onUpdate({ crop: { top: Math.round(srcH * 0.025 / 2) * 2, bottom: crop?.bottom || 0, left: crop?.left || 0, right: crop?.right || 0 } })}>Статус-бар сверху</Button>
            {onFitCanvas && hasCrop && <Button size="compact-xs" variant="outline" color="blue" onClick={() => onFitCanvas(effW, effH)}>Холст ({effW}×{effH})</Button>}
          </Group>
          <CropSliders crop={crop} srcW={srcW} srcH={srcH} onChange={c => onUpdate({ crop: c })} />
        </Stack>
      )}

      {/* Секция замазок */}
      {maskOpen && (
        <Stack gap="xs" mt="sm" p="xs" style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 'var(--mantine-radius-sm)', border: '1px solid var(--mantine-color-default-border)' }}>
          <Group justify="space-between" align="center">
            <Group gap={6}>
              <Paintbrush size={15} style={{ color: '#20c997' }} />
              <Text fz="xs" fw={700} tt="uppercase" c="teal">Замазка лекции (Маскирование) · {masks.length}</Text>
            </Group>
            <Button variant="light" color="teal" size="compact-xs" leftSection={<Plus size={13} />} onClick={addMask}>+ Замазка</Button>
          </Group>
          {masks.map((m, idx) => {
            const isCurEditing = isMasking && activeMaskId === m.id;
            return (
              <Stack key={m.id} gap="xs" p="xs" style={{ background: isCurEditing ? 'rgba(32, 201, 151, 0.12)' : 'var(--mantine-color-default)', borderRadius: 'var(--mantine-radius-sm)', border: isCurEditing ? '1px solid #20c997' : '1px solid var(--mantine-color-default-border)' }}>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap={6} wrap="nowrap">
                    <Text fw={700} fz="xs" c="teal">#{idx + 1}</Text>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: m.color || '#000', border: '1px solid #fff' }} />
                    <Text fz="xs" fw={500}>{m.image ? 'Трафарет нарисован' : 'Пустой трафарет'}</Text>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    <Button
                      variant={isCurEditing ? 'filled' : 'light'}
                      color="teal"
                      size="compact-xs"
                      leftSection={<Paintbrush size={12} />}
                      onClick={() => onToggleMasking?.(m.id)}
                    >
                      {isCurEditing ? 'Рисование...' : 'Рисовать'}
                    </Button>
                    <Tooltip label="Удалить замазку" withArrow>
                      <ActionIcon variant="subtle" color="red" size="sm" onClick={() => deleteMask(m.id)}>
                        <Trash2 size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
                {/* Строка 1: Таймкоды и кнопка "По слайду" */}
                <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
                  <Group gap={4} wrap="nowrap" align="center">
                    <Text fz="xs" c="dimmed">Кадры:</Text>
                    <InlineTime value={m.start} onCommit={v => patchMask(m.id, { start: v })} />
                    <Text c="dimmed">—</Text>
                    <InlineTime value={m.end} onCommit={v => patchMask(m.id, { end: v })} />
                  </Group>
                  <Tooltip label="Растянуть замазку на всё время показа слайда" withArrow openDelay={200}>
                    <Button
                      variant="light"
                      color="violet"
                      size="compact-xs"
                      px={8}
                      loading={detectingMaskId === m.id}
                      leftSection={<Sparkles size={13} />}
                      onClick={() => onAutoDetectSlide?.(m.id)}
                    >
                      По слайду
                    </Button>
                  </Tooltip>
                </Group>

                {/* Строка 2: Вспомогательные кнопки ручной границы */}
                <Group justify="flex-start" align="center" gap={6} wrap="nowrap">
                  <Text fz="11px" c="dimmed">Граница:</Text>
                  <Tooltip label="Установить начало замазки на текущую позицию плеера" withArrow openDelay={200}>
                    <Button variant="subtle" color="teal" size="compact-xs" px={6} onClick={() => patchMask(m.id, { start: formatTime(currentTime) })}>
                      С курсора
                    </Button>
                  </Tooltip>
                  <Tooltip label="Установить конец замазки на текущую позицию плеера" withArrow openDelay={200}>
                    <Button variant="subtle" color="teal" size="compact-xs" px={6} onClick={() => patchMask(m.id, { end: formatTime(currentTime) })}>
                      До курсора
                    </Button>
                  </Tooltip>
                </Group>
              </Stack>
            );
          })}
        </Stack>
      )}

      {overlays.length > 0 && (
        <Stack gap="xs" mt="sm">
          <Group justify="space-between" align="center">
            <Group gap={6}><Video size={16} style={{ color: 'var(--mantine-color-pink-5)' }} /><Text fz="xs" fw={700} tt="uppercase" c="dimmed">Камеры лектора · {overlays.length}</Text></Group>
            <FolderSelectMenu label="Добавить камеру" text="+ Камера" icon={<Plus size={14} />} mediaBin={mediaBin} onSelectBin={addOverlay} onBrowseDisk={onBrowseOverlay} color="blue" />
          </Group>
          {overlays.map((ov, idx) => {
            const ovId = ov.id || String(idx), isColor = Boolean(ov.color_enabled), ovCrop = ov.crop;
            const camBin = mediaBin.find(x => x.path === ov.video), camW = camBin?.width || 1920, camH = camBin?.height || 1080;
            return (
              <Stack key={ovId} gap="xs" p="xs" style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 'var(--mantine-radius-sm)', border: '1px solid var(--mantine-color-default-border)' }}>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}><Text fw={700} fz="xs" c="pink">#{idx + 1}</Text><Text fw={600} fz="xs" truncate>{ov.video ? fileName(ov.video) : 'Выберите видео'}</Text></Group>
                  <Group gap={2} wrap="nowrap"><FolderSelectMenu label="Сменить" tooltip="Заменить файл камеры" icon={<Folder size={14} />} mediaBin={mediaBin} onSelectBin={p => patchOverlay(ovId, { video: p })} onBrowseDisk={onBrowseOverlay} /><Tooltip label="Убрать камеру" withArrow openDelay={300}><ActionIcon variant="subtle" color="red" size="sm" aria-label="Убрать камеру" onClick={() => onUpdate({ overlays: overlays.filter(o => o.id !== ovId), overlay: overlays.filter(o => o.id !== ovId)[0] || null })}><Trash2 size={14} /></ActionIcon></Tooltip></Group>
                </Group>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <TimeRange label="На лекции" start={ov.timeline_start || seg.start || '00:00:00'} end={ov.timeline_end || seg.end || '00:00:00'} onStart={v => patchOverlay(ovId, { timeline_start: v })} onEnd={v => patchOverlay(ovId, { timeline_end: v })} />
                  <Button variant="light" color="violet" size="compact-xs" leftSection={<Zap size={13} />} onClick={() => onAutoSync?.(seg.audio || seg.video, ov.video, seg.id, ovId)}>Синхронизировать</Button>
                </Group>
                <TimeRange label="В файле" start={ov.start} end={ov.end} placeholderEnd="конец" onStart={v => patchOverlay(ovId, { start: v })} onEnd={v => patchOverlay(ovId, { end: v })} />
                <Checkbox size="xs" checked={ov.mix_audio} label="Микшировать звук камеры" onChange={e => patchOverlay(ovId, { mix_audio: e.currentTarget.checked })} />
                <Group justify="space-between" align="center" mt={2}>
                  <Text fz="xs" fw={500} c="dimmed">Привязка к углу:</Text>
                  <Group gap={4} wrap="nowrap">
                    {PIP_CORNERS.map(({ corner, label }) => {
                      const Icon = corner === 'top-left' ? ArrowUpLeft : corner === 'top-right' ? ArrowUpRight : corner === 'bottom-left' ? ArrowDownLeft : ArrowDownRight;
                      return (
                        <Tooltip key={corner} label={label} withArrow>
                          <ActionIcon size="sm" variant={isOverlaySnapped(ov, corner, camW, camH, canvasWidth, canvasHeight) ? 'filled' : 'subtle'} color="pink" onClick={() => snapOverlay(ov, corner)}>
                            <Icon size={14} />
                          </ActionIcon>
                        </Tooltip>
                      );
                    })}
                  </Group>
                </Group>
                <Divider mt={2} mb={2} />
                <Group justify="space-between" align="center"><Text fz="xs" fw={600}>Кадрирование камеры</Text>{Boolean(ovCrop?.top || ovCrop?.bottom || ovCrop?.left || ovCrop?.right) && <Button variant="subtle" color="gray" size="compact-xs" onClick={() => patchOverlay(ovId, { crop: null })}>Сброс</Button>}</Group>
                <CropSliders crop={ovCrop} srcW={camW} srcH={camH} onChange={c => patchOverlay(ovId, { crop: c })} />
                <Divider mt={2} mb={2} />
                <Group justify="space-between" align="center">
                  <Switch size="xs" label="Цветокоррекция" checked={isColor} onChange={e => patchOverlay(ovId, { color_enabled: e.currentTarget.checked })} />
                  <Group gap={4} wrap="nowrap">
                    <Button variant="light" color="violet" size="compact-xs" leftSection={<Wand2 size={13} />} disabled={!ov.video} onClick={() => onAutoColorGrade?.(ov.video, seg.id, ovId, ov.crop)}>Авто</Button>
                    {isColor && <Button variant="subtle" color="gray" size="compact-xs" onClick={() => patchOverlay(ovId, { lift: 0, gamma: 1, gain: 1 })}>Сброс</Button>}
                  </Group>
                </Group>
                {isColor && (
                  <Stack gap={6} mt="xs">
                    {COLOR_SLIDERS.map(({ key, label, tip, min, max, step, def, sign }) => {
                      const val = (ov as any)[key] ?? def;
                      return (
                        <Group key={key} gap="xs" wrap="nowrap" align="center">
                          <Tooltip label={tip} withArrow position="left"><Text fz="xs" w={55} style={{ flexShrink: 0, cursor: 'help' }}>{label}:</Text></Tooltip>
                          <Slider style={{ flex: 1 }} size="xs" min={min} max={max} step={step} value={val} label={v => (sign && v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2))} onChange={v => patchOverlay(ovId, { [key]: v })} />
                          <Text fz="xs" c="dimmed" w={42} ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{val.toFixed(2)}</Text>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
